import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { INDUSTRIES } from "../data/industries";

const Industries = () => {
    const [active, setActive] = useState(INDUSTRIES[0].slug);
    const current = INDUSTRIES.find((i) => i.slug === active) || INDUSTRIES[0];

    return (
        <main data-testid="industries-page" className="bg-white text-[#050B14]">
            {/* HERO */}
            <section className="relative bg-[#050B14] text-white pt-32 pb-24 overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-40" />
                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="max-w-3xl">
                        <div className="kicker text-[#00E5FF] mb-6 flex items-center gap-3">
                            <span className="w-8 h-px bg-[#00E5FF]" /> Industries
                        </div>
                        <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                            Sectors where ELWIS <span className="text-gradient-cyan">already fits</span> today.
                        </h1>
                        <p className="mt-8 text-lg text-white/70 leading-relaxed max-w-2xl">
                            Six sectors where our current 11 kW wireless AC and DC platform
                            solves real, day-to-day commercial charging problems.
                        </p>
                    </div>
                </div>
            </section>

            {/* INTERACTIVE EXPLORER */}
            <section data-testid="industries-explorer" className="py-24 lg:py-32 bg-[#FAFAFA]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="grid lg:grid-cols-12 gap-10">
                        {/* Industry list */}
                        <div className="lg:col-span-4">
                            <div className="kicker text-[#0055FF] mb-4">Select industry</div>
                            <div className="border border-slate-200 bg-white">
                                {INDUSTRIES.map((ind) => {
                                    const isActive = ind.slug === active;
                                    return (
                                        <button
                                            key={ind.slug}
                                            type="button"
                                            data-testid={`industry-select-${ind.slug}`}
                                            onClick={() => setActive(ind.slug)}
                                            className={`w-full text-left px-5 py-4 border-b border-slate-200 last:border-b-0 transition-colors flex items-center justify-between gap-4 ${
                                                isActive
                                                    ? "bg-[#050B14] text-white"
                                                    : "hover:bg-slate-50 text-[#0A0F1C]"
                                            }`}
                                        >
                                            <span className="font-display font-semibold">{ind.title}</span>
                                            <ArrowRight
                                                className={`w-4 h-4 transition-transform ${
                                                    isActive ? "text-[#00E5FF] translate-x-1" : "text-slate-400"
                                                }`}
                                            />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Detail panel */}
                        <div className="lg:col-span-8" data-testid="industry-detail-panel">
                            <div className="border border-slate-200 bg-white overflow-hidden">
                                <div
                                    className="aspect-[16/8] bg-cover bg-center relative"
                                    style={{ backgroundImage: `url(${current.image})` }}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#050B14]/80 via-[#050B14]/20 to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-8">
                                        <div className="kicker text-[#00E5FF] mb-3">Industry</div>
                                        <h2 className="font-display font-black text-3xl lg:text-5xl text-white tracking-tight">
                                            {current.title}
                                        </h2>
                                    </div>
                                </div>

                                <div className="grid lg:grid-cols-2 gap-px bg-slate-200">
                                    <div className="bg-white p-8">
                                        <div className="kicker text-[#0055FF] mb-3">Challenge</div>
                                        <p className="font-display text-lg text-[#0A0F1C] leading-snug">
                                            {current.problem}
                                        </p>
                                    </div>
                                    <div className="bg-white p-8">
                                        <div className="kicker text-[#0055FF] mb-3">ELWIS advantage</div>
                                        <p className="text-slate-600 leading-relaxed">
                                            {current.advantage}
                                        </p>
                                    </div>
                                </div>

                                <div className="p-8 border-t border-slate-200">
                                    <div className="kicker text-[#0055FF] mb-5">Use cases</div>
                                    <div className="flex flex-wrap gap-2">
                                        {current.useCases.map((u) => (
                                            <span
                                                key={u}
                                                className="px-4 py-2 border border-slate-300 text-sm font-mono text-[#0A0F1C] hover:border-[#0055FF] hover:text-[#0055FF] transition-colors"
                                            >
                                                {u}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className="p-8 bg-[#050B14] text-white flex flex-col sm:flex-row sm:items-center justify-between gap-5">
                                    <div>
                                        <div className="kicker text-[#00E5FF] mb-2">Business value</div>
                                        <p className="text-white/80 max-w-xl">
                                            Lower operating cost · reduced labor · higher uptime ·
                                            cleaner automation · longer lifecycle.
                                        </p>
                                    </div>
                                    <Link
                                        to="/contact"
                                        data-testid="industry-detail-cta"
                                        className="inline-flex items-center gap-2 bg-[#00E5FF] text-[#050B14] px-5 py-3 rounded-md font-semibold hover:bg-white transition-colors shrink-0"
                                    >
                                        Discuss this use case
                                        <ArrowUpRight className="w-4 h-4" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* OVERVIEW GRID */}
            <section className="py-24 lg:py-32 bg-white border-t border-slate-100">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="max-w-2xl mb-12">
                        <div className="kicker text-[#0055FF] mb-5">All sectors</div>
                        <h2 className="font-display font-bold text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#0A0F1C]">
                            One wireless charging platform. Multiple commercial applications.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                        {INDUSTRIES.map((ind, idx) => (
                            <button
                                key={ind.slug}
                                type="button"
                                onClick={() => {
                                    setActive(ind.slug);
                                    document
                                        .querySelector('[data-testid="industries-explorer"]')
                                        ?.scrollIntoView({ behavior: "smooth", block: "start" });
                                }}
                                data-testid={`industry-tile-${ind.slug}`}
                                className="bg-white p-8 text-left group hover:bg-[#050B14] hover:text-white transition-all"
                            >
                                <div className="flex items-center justify-between mb-5">
                                    <span className="font-mono text-xs text-slate-400 group-hover:text-[#00E5FF]">
                                        {String(idx + 1).padStart(2, "0")} / {String(INDUSTRIES.length).padStart(2, "0")}
                                    </span>
                                    <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-[#00E5FF] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                                </div>
                                <h3 className="font-display font-bold text-xl lg:text-2xl tracking-tight">{ind.title}</h3>
                                <p className="mt-3 text-sm text-slate-600 group-hover:text-white/70 leading-relaxed">{ind.short}</p>
                            </button>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Industries;
